import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from 'config/apiConfig';
import { getTokenStatus } from "utils/tokenManager";

const TRANSITION_DURATION = 300;

function AlarmModal({ isOpen, onClose }) {
    const [isVisible, setIsVisible] = useState(isOpen);
    const [isAnimating, setIsAnimating] = useState(false);
    const [alarmList, setAlarmList] = useState([]);

    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            requestAnimationFrame(() => setIsAnimating(true));
            fetchPushList();
        } else {
            setIsAnimating(false);
            const timer = setTimeout(() => setIsVisible(false), TRANSITION_DURATION);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // 알림 목록 조회
    const fetchPushList = async () => {
        try {
            const { token } = getTokenStatus();
            const response = await fetch(`${API_BASE_URL}/pushList`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (response.ok) {
                const data = await response.json();
                const datalist = data.data || data;

                if (datalist.list && Array.isArray(datalist.list)) {
                    setAlarmList(datalist.list);
                }
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    // 개별 알림 읽음 처리
    const handleReadItem = async (key) => {
        if (!key) return;

        try {
            const { token } = getTokenStatus();
            const response = await fetch(`${API_BASE_URL}/pushView`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`,
                },
                body: new URLSearchParams({ key: key })
            });

            if (response.ok) {
                // 성공 시 로컬 상태 업데이트 (즉시 '읽음' 스타일 적용)
                setAlarmList(prevList =>
                    prevList.map(item =>
                        // 서버에서 'read' 값을 "Y"로 쓰므로 로컬도 "Y"로 변경
                        item.key === key ? { ...item, read: "Y" } : item
                    )
                );
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    if (!isVisible) return null;

    return (
        <div style={{ ...styles.overlay, opacity: isAnimating ? 1 : 0 }} onClick={onClose}>
            <div style={{ ...styles.modalContent, transform: isAnimating ? 'translateY(0)' : 'translateY(100%)' }}
                onClick={(e) => e.stopPropagation()}>

                <div style={styles.header}>
                    <h2 style={styles.title}>PUSH 알림</h2>
                    <button style={styles.closeButton} onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18" stroke="#FCFCFA" strokeWidth="2" strokeLinecap="round"
                                strokeLinejoin="round" />
                            <path d="M6 6L18 18" stroke="#FCFCFA" strokeWidth="2" strokeLinecap="round"
                                strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>

                <div style={styles.listContainer}>
                    {alarmList.length > 0 ? (
                        alarmList.map((item, index) => {
                            // 읽음 여부 판단 (서버 데이터: "Y" or "N" 가정)
                            const isRead = item.read === "Y";

                            // [핵심] 상태에 따른 동적 스타일 생성
                            const dynamicStyles = getDynamicStyles(isRead);

                            return (
                                <div key={item.key || index} style={dynamicStyles.container}>
                                    <div style={styles.itemHeader}>
                                        <div style={styles.titleWrapper}>
                                            <span style={dynamicStyles.title}>알림</span>
                                            <span style={styles.itemDate}>{item.date}</span>
                                        </div>

                                        {/* 읽지 않았을 때만 '읽음' 버튼 노출 */}
                                        {!isRead ? (
                                            <button
                                                style={styles.readButton}
                                                onClick={() => handleReadItem(item.key)}
                                            >
                                                읽음
                                            </button>
                                        ) : (
                                            <span style={styles.readCompleteText}>읽음 완료</span>
                                        )}
                                    </div>
                                    <p style={dynamicStyles.message}>
                                        {item.cont}
                                    </p>
                                </div>
                            );
                        })
                    ) : (
                        <div style={styles.emptyMessage}>
                            새로운 알림이 없습니다.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// [핵심] 읽음/안읽음 상태에 따른 스타일 반환 함수
const getDynamicStyles = (isRead) => {
    return {
        container: {
            backgroundColor: '#1B1931',
            borderRadius: '12px',
            padding: '16px',
            position: 'relative',
            border: '1px solid #3A394A',
            // 읽은 메시지: 전체 투명도 0.5로 흐리게 처리
            opacity: isRead ? 0.5 : 1,
            transition: 'opacity 0.3s ease'
        },
        title: {
            fontFamily: 'Pretendard',
            // 읽은 메시지: 보통 굵기(500), 안 읽은 메시지: 두꺼운 굵기(700)
            fontWeight: isRead ? '500' : '700',
            fontSize: '16px',
            color: '#8052FF',
            marginBottom: '4px'
        },
        message: {
            fontFamily: 'Pretendard',
            // 읽은 메시지: 보통 굵기(400), 안 읽은 메시지: 약간 두껍게(500)
            fontWeight: isRead ? '400' : '500',
            fontSize: '14px',
            lineHeight: '1.5',
            margin: 0,
            // 읽은 메시지: 회색조, 안 읽은 메시지: 흰색
            color: isRead ? '#9A9A9A' : '#FCFCFA'
        }
    };
};

const styles = {
    overlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)', zIndex: 2000,
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        transition: `opacity ${TRANSITION_DURATION}ms ease-in-out`
    },
    modalContent: {
        backgroundColor: '#1B1931', borderTopLeftRadius: '20px', borderTopRightRadius: '20px',
        width: '100%', maxWidth: '430px', height: '80vh', margin: '0 auto',
        padding: '24px 24px 40px', boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column',
        transition: `transform ${TRANSITION_DURATION}ms cubic-bezier(0.25, 1, 0.5, 1)`
    },
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px'
    },
    title: {
        fontFamily: 'Pretendard', fontWeight: '700', fontSize: '20px', color: '#FCFCFA', margin: 0
    },
    closeButton: {
        background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
    },
    listContainer: {
        flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column',
        gap: '12px', paddingBottom: '24px', scrollbarWidth: 'none', msOverflowStyle: 'none'
    },
    itemHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'
    },
    titleWrapper: {
        display: 'flex', alignItems: 'center', gap: '8px'
    },
    itemDate: { fontFamily: 'Pretendard', fontWeight: '400', fontSize: '12px', color: '#747474' },
    readButton: {
        backgroundColor: 'rgba(128, 82, 255, 0.1)',
        color: '#8052FF',
        border: '1px solid #8052FF',
        borderRadius: '4px',
        padding: '4px 8px',
        fontSize: '12px',
        fontFamily: 'Pretendard',
        fontWeight: '500',
        cursor: 'pointer',
    },
    readCompleteText: {
        color: '#5F5E68',
        fontSize: '12px',
        fontFamily: 'Pretendard',
        fontWeight: '400',
    },
    emptyMessage: {
        textAlign: 'center',
        color: '#747474',
        marginTop: '50px',
        fontFamily: 'Pretendard',
        fontSize: '14px'
    }
};

export default AlarmModal;